/**
 * EMP-WS-UI-001 / HR-AUDIT-UI-001 — activity feed rendered from hr_audit_log rows
 * (action, changes[], reason, summary, changed_by_name, changed_at, employee_id, subject_type).
 * Shared by Employee Profile / Edit › Activity and the standalone HR Audit Log page.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import {
  History, IdCard, Mail, UserPlus, Link2, Unlink, Pencil, Camera, Clock3, Activity, ArrowRight, Eye, Scale,
  Download, Upload, Settings2, Coffee, BellRing, CheckCircle2, ShieldOff, Laptop, Server, UserCircle2,
} from 'lucide-react';
import { formatDateTimeLocal, formatDateOnly } from '../../../../utils/formatters';

const T = (icon, cls, label, kind) => ({ icon, cls, label, kind });
const V = 'bg-violet-50 text-violet-600 ring-violet-100', B = 'bg-blue-50 text-blue-600 ring-blue-100', E = 'bg-emerald-50 text-emerald-600 ring-emerald-100';
const A = 'bg-amber-50 text-amber-600 ring-amber-100', R = 'bg-rose-50 text-rose-600 ring-rose-100', S = 'bg-slate-100 text-slate-600 ring-slate-100', K = 'bg-sky-50 text-sky-600 ring-sky-100', I = 'bg-indigo-50 text-indigo-600 ring-indigo-100';

export const EVENT_TYPES = {
  create: T(UserPlus, E, 'Employee Created'), created: T(UserPlus, E, 'Employee Created'),
  update: T(Pencil, S, 'Record Updated'), updated: T(Pencil, S, 'Record Updated'),
  status_change: T(Activity, A, 'Status Changed'), status_changed: T(Activity, A, 'Status Changed'),
  config_update: T(Settings2, V, 'Config Updated'),
  employee_id_changed: T(IdCard, V, 'Employee ID Changed'),
  work_email_changed: T(Mail, B, 'Work Email Changed'),
  shift_update: T(Clock3, I, 'Shift Updated'), shift_updated: T(Clock3, I, 'Shift Updated'),
  user_account_linked: T(Link2, E, 'User Account Linked'), user_linked: T(Link2, E, 'User Account Linked'),
  user_account_relinked: T(Link2, B, 'User Account Re-linked'),
  user_account_unlinked: T(Unlink, R, 'User Account Unlinked'), user_unlinked: T(Unlink, R, 'User Account Unlinked'),
  bulk_link_user: T(Link2, E, 'Bulk Account Link'),
  photo_updated: T(Camera, K, 'Photo Updated'), photo_removed: T(Camera, S, 'Photo Removed'),
  sensitive_view: T(Eye, A, 'Sensitive Data Viewed', 'access'),
  leave_balance_adjustment: T(Scale, I, 'Leave Balance Adjustment', 'leave'),
  export: T(Download, S, 'Bulk Export', 'system'), import: T(Upload, S, 'Bulk Import', 'system'),
  import_create: T(Upload, E, 'Imported (Created)'), import_update: T(Upload, B, 'Imported (Updated)'),
  it_offboarding_triggered: T(ShieldOff, A, 'IT Offboarding Triggered'), it_offboarding_cancelled: T(ShieldOff, S, 'IT Offboarding Cancelled'),
  it_clearance_completed: T(CheckCircle2, E, 'IT Clearance Completed'),
  it_asset_recovery_escalated: T(Laptop, R, 'Asset Recovery Escalated'), it_asset_recovery_resolved: T(Laptop, E, 'Asset Recovery Resolved'),
  // break / user-activity events (subject_type=user)
  started: T(Coffee, E, 'Break Started', 'break'), ended: T(Coffee, B, 'Break Ended', 'break'),
  auto_ended: T(Coffee, A, 'Break Auto-Ended', 'break'), force_ended: T(Coffee, R, 'Break Force-Ended', 'break'),
  acknowledged: T(CheckCircle2, V, 'Break Acknowledged', 'break'), reminder_sent: T(BellRing, A, 'Break Reminder Sent', 'break'),
};

export const typeFor = (a) => {
  const key = (a.action || '').toLowerCase();
  if (EVENT_TYPES[key]) return { ...EVENT_TYPES[key], kind: EVENT_TYPES[key].kind || (a.subject_type === 'user' ? 'break' : 'hr') };
  if (key.includes('status') || (a.changes || []).some(c => c.field === 'status')) return EVENT_TYPES.status_change;
  return T(Pencil, S, key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Activity', a.subject_type === 'user' ? 'break' : 'hr');
};

const nice = (f) => (f || '').replace(/_/g, ' ').replace(/:/g, ' · ').replace(/\b\w/g, c => c.toUpperCase());
const val = (v) => (v === null || v === undefined || v === '') ? '—' : formatDateOnly(String(v)) || String(v);
const isBlank = (v) => v === null || v === undefined || v === '';
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Subject chip: employee link · system record · user-account keyed record. */
const SubjectChip = ({ a, t }) => {
  const id = a.employee_id;
  if (t.kind === 'system' || id === 'BULK') return <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-1.5 py-0.5 text-[11.5px] font-semibold text-slate-600" data-testid="activity-subject-system"><Server className="h-3 w-3" /> System · all employees</span>;
  if (t.kind === 'break') return <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-1.5 py-0.5 text-[11.5px] font-semibold text-slate-600" data-testid="activity-subject-user"><UserCircle2 className="h-3 w-3" /> {id}</span>;
  if (t.kind === 'leave' || UUID_RE.test(id || '')) return <span className="inline-flex items-center gap-1 rounded-md bg-indigo-50 px-1.5 py-0.5 text-[11.5px] font-semibold text-indigo-700" title={`User account ${id}`} data-testid="activity-subject-account"><UserCircle2 className="h-3 w-3" /> User account · {String(id).slice(0, 8)}…</span>;
  return <Link to={`/admin/hr/employees/${id}`} className="inline-flex items-center gap-1 rounded-md bg-violet-50 px-1.5 py-0.5 font-mono text-[11.5px] font-semibold text-violet-700 hover:bg-violet-100 transition-colors" data-testid="activity-subject-employee">{id}</Link>;
};

const NOTE = {
  system: 'System-level record — a bulk operation across many employees, not tied to a single profile.',
  leave: 'Recorded against the linked user account (leave balances live in Leave Management, not the HR record).',
  access: 'Access log — records who viewed restricted fields; no data was changed.',
  break: 'User activity event from the attendance/break system.',
};

export const ActivityTimeline = ({ rows, testid = 'ws-activity-list', emptyText = 'No activity recorded.', showSubject = false, formatTime = formatDateTimeLocal }) => (
  <div data-testid={testid}>
    {rows.length === 0 && (
      <div className="flex flex-col items-center py-10 text-center"><span className="h-11 w-11 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center"><History className="h-5 w-5" /></span><p className="text-[13px] text-slate-400 mt-3">{emptyText}</p></div>
    )}
    <ol className="relative ml-4 border-l border-slate-200/80 space-y-1">
      {rows.map((a, i) => {
        const t = typeFor(a); const Icon = t.icon;
        const changes = (a.changes || []).filter(c => !(isBlank(c.old_value) && isBlank(c.new_value)) && c.old_value !== c.new_value);
        const note = showSubject && NOTE[t.kind];
        return (
          <li key={a.id || i} className="an-rise relative pl-7 py-3" style={{ animationDelay: `${Math.min(i, 8) * 40}ms` }} data-testid="activity-item">
            <span className={`absolute -left-[15px] top-3 h-[30px] w-[30px] rounded-full ring-4 ring-white flex items-center justify-center ${t.cls}`}><Icon className="h-3.5 w-3.5" /></span>
            <div className="lv-lift rounded-xl border border-slate-100 bg-white px-4 py-3">
              <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-[13.5px] font-semibold text-slate-900" data-testid="activity-action">{t.label}</p>
                  {showSubject && <SubjectChip a={a} t={t} />}
                </div>
                <p className="text-[12px] text-slate-400 whitespace-nowrap"><span className="font-medium text-slate-500">{a.changed_by_name || 'System'}</span> • {a.changed_at ? formatTime(a.changed_at) : ''}</p>
              </div>
              {a.summary && <p className="mt-1 text-[13px] text-slate-700" data-testid="activity-summary">{a.summary}</p>}
              {changes.length > 0 ? (
                <ul className="mt-1.5 space-y-1">
                  {changes.map((c, j) => (
                    <li key={j} className="flex flex-wrap items-center gap-x-2 text-[13px]" data-testid="activity-change">
                      <span className="text-slate-500">{nice(c.field)}</span>
                      {!isBlank(c.old_value) && <><span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-slate-600 line-through decoration-slate-300 break-all">{val(c.old_value)}</span><ArrowRight className="h-3.5 w-3.5 text-slate-300" /></>}
                      <span className="rounded-md bg-violet-50 px-1.5 py-0.5 font-semibold text-violet-700 break-all">{val(c.new_value)}</span>
                    </li>
                  ))}
                </ul>
              ) : (!a.summary && a.details) ? <p className="mt-1 text-[13px] text-slate-600">{String(a.details)}</p> : null}
              {a.reason && <p className="mt-1.5 text-[12.5px] text-slate-500"><span className="font-medium text-slate-600">Reason:</span> {a.reason}</p>}
              {note && <p className="mt-1.5 text-[11.5px] text-slate-400 italic" data-testid="activity-note">{note}</p>}
            </div>
          </li>
        );
      })}
    </ol>
  </div>
);
